/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../locales/en-US/descriptors';
import {
  isObjectOrArrayAndEmpty,
  returnFormattedDate
} from '../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../shared/ErrorComponents/ConnectionError';
import withMSLoadingStatus from '../../shared/MSStatusWrappers/MSLoadingStatus';
import withMSErrorStatus from '../../shared/MSStatusWrappers/MSErrorStatus';
import ISiteEnergyUsageHOCProps from './VirtualMeterEnergyUsageHOCProps';
import useMounted from '../../shared/useMount/useMount';
import VirtualMeterEnergyUsageComponent from '../VirtualMeterEnergyUsageComponent/VirtualMeterEnergyUsageComponent';
import { EnergyUsageField } from '../../shared/Enums';
import {
  apiNoContentStatusCode,
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  POLLING_MILLI_SECONDS,
  POLLING_SECONDS
} from '../../shared/Utils/Constants';
import { getProgressBarUnitScaledData } from '../../shared/Utils/portfolioDataFormatter';
import { ConversionType } from '../../shared/Utils/unitHandling/UnitModel';
import { useInterval } from '../../shared/useInterval/useInterval';
import { energyProgressbarProps } from '../../shared/ProgressBar/IProgressBarWidget';
import {
  IEmaasEnergyUsageComponentData,
  ISiteCard
} from '../../DashboardKPIWidgets/IEnergyManagementProps';

interface ISiteEnergyUsageHOCContent extends ISiteCard {
  data: IEmaasEnergyUsageComponentData;
}

const VirtualMeterEnergyUsageHOCContent = (
  props: ISiteEnergyUsageHOCContent
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  const {
    energyMeasurementUnit,
    summary: {
      totalConsumption,
      totalBaselineConsumption,
      totalEnergyDeviation,
      totalEnergyDeviationPercentage,
      absoluteTotalConsumption,
      absoluteTotalBaselineConsumption
    }
  } = data;

  const progressBarUnitScaledData = getProgressBarUnitScaledData(
    ConversionType.ELECTRICITY,
    { previousPeriod: totalBaselineConsumption, actual: totalConsumption },
    totalEnergyDeviation,
    energyMeasurementUnit,
    undefined,
    absoluteTotalBaselineConsumption,
    absoluteTotalConsumption
  );

  const aggregationSingleSiteEnergyUsageData: energyProgressbarProps = {
    actual: progressBarUnitScaledData.actual,
    previousPeriod: progressBarUnitScaledData.previousPeriod,
    savings: progressBarUnitScaledData.deviation,
    savingsPercentage: totalEnergyDeviationPercentage
      ? parseFloat(
          totalEnergyDeviationPercentage?.toFixed(
            KPI_PERCENTAGE_DECIMAL_PRECISION
          )
        )
      : null,
    absoluteActual: progressBarUnitScaledData.absoluteActual,
    absolutePrevPeriod: progressBarUnitScaledData.absolutePreviousPeriod
  };

  return (
    <div>
      <VirtualMeterEnergyUsageComponent
        unit={progressBarUnitScaledData.ConvertedUnit}
        category={EnergyUsageField.ENERGY_USAGE}
        aggregationData={aggregationSingleSiteEnergyUsageData}
      />
    </div>
  );
};

const VirtualMeterEnergyUsageHOCWithLoader = withMSLoadingStatus(
  withMSErrorStatus(VirtualMeterEnergyUsageHOCContent)
);

const ENERGYUSAGE_SITE_REFRESH_INTERVAL =
  POLLING_SECONDS * POLLING_MILLI_SECONDS;

const VirtualMeterEnergyUsageHOC = (props: ISiteEnergyUsageHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const [pollingCount, setPollingCount] = useState(0);
  const { bearerToken, requestContext } = props;
  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);

  const energyUsageConnectorConfig = {
    name: 'energy-usage-site',
    options: {
      startDate: requestContext.context.startTime
        ? returnFormattedDate(requestContext.context.startTime)
        : '',
      endDate: requestContext.context.endTime
        ? returnFormattedDate(requestContext.context.endTime)
        : ''
    }
  };
  const isMounted = useMounted();
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig: energyUsageConnectorConfig,
    requestContext,
    bearerToken
  });

  const onRefresh = () => dataConnector?.actions?.getAll();

  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [requestContext, pollingCount]);

  const { all, loading, error, status } = dataConnector.data;
  const EmaasEnergyUsage = (all as unknown) as IEmaasEnergyUsageComponentData;

  useInterval(() => {
    if (
      EmaasEnergyUsage &&
      EmaasEnergyUsage.subscriptionType === EnergyUsageField.PREMIUMSUBS
    ) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_SITE_REFRESH_INTERVAL);

  const [errorInfo, setErrorInfo] = useState(error);
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);

  const SingleSiteEnergyUsageSavings = (all as unknown) as IEmaasEnergyUsageComponentData;
  const isDataAvailable = SingleSiteEnergyUsageSavings ? true : false;

  const [data, setData] = useState({} as IEmaasEnergyUsageComponentData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(SingleSiteEnergyUsageSavings);
      }
      setStatusCode(status);
      setErrorInfo(error);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <VirtualMeterEnergyUsageHOCWithLoader
      loading={isLoading}
      error={errorInfo}
      statusCode={statusCode}
      onRefresh={removeSilentLoadingonRefresh}
      data={data}
      loadingText={t(d.LOADING)}
      title={t(d.CONNECTIONERRORTITLE)}
      noDataMessage={t(d.NODATAAVAILABLE)}
      connectionErrorMessage={t(d.CONNECTIONERROR)}
      isNoContent={status === apiNoContentStatusCode}
      silentLoading={silentLoading}
    />
  );
};
export default VirtualMeterEnergyUsageHOC;
